import { useEffect, useRef } from 'react';

const oldSteps = [
  { num: 'STEP 1', title: 'Roof Cap에 볼트 체결', icon: 'ri-tools-fill', deleted: false },
  { num: 'STEP 2', title: '캡 뚜껑 닫기', icon: 'ri-box-3-fill', deleted: false },
  { num: 'STEP 3', title: '실리콘 보조도구 준비', sub: '실리콘, 건, 헤라', icon: 'ri-paint-fill', deleted: true },
  { num: 'STEP 4', title: '실리콘 도포작업', sub: '수작업', icon: 'ri-brush-fill', deleted: true },
  { num: 'STEP 5', title: '경화 대기', sub: '24시간 이상', icon: 'ri-time-fill', deleted: true },
];

const newSteps = [
  { num: 'STEP 1', title: 'Smart Cap에 볼트 체결', sub: '패킹 자동 압착', icon: 'ri-tools-fill' },
  { num: 'STEP 2', title: '캡 뚜껑 닫기', sub: '→ 끝!', icon: 'ri-checkbox-circle-fill', isLast: true },
];

const ProcessComparisonSection = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('opacity-100', 'translate-y-0');
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionRef.current?.querySelectorAll('.animate-on-scroll');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section className="py-24 bg-white w-full" ref={sectionRef}>
      <div className="max-w-6xl mx-auto px-6 lg:px-8">

        {/* 헤더 */}
        <div className="animate-on-scroll opacity-0 translate-y-6 transition-all duration-700 text-center mb-4">
          <span className="inline-flex items-center gap-2 bg-[#1B2A4A]/8 text-[#1B2A4A] text-xs font-bold tracking-widest uppercase px-5 py-1.5 rounded-full border border-[#1B2A4A]/15 mb-6">
            <i className="ri-arrow-left-right-line" />
            PROCESS COMPARISON
          </span>
          <h2 className="text-3xl md:text-4xl font-extrabold text-[#1B2A4A] mt-4 mb-3">
            시공 프로세스 비교
          </h2>
          <p className="text-gray-500 text-base md:text-lg">
            작업공정 단계 수가 곧 <span className="font-semibold text-[#1B2A4A]">시간</span>이고,{' '}
            <span className="font-semibold text-[#1B2A4A]">비용</span>입니다
          </p>
        </div>

        {/* 2컬럼 비교 */}
        <div className="animate-on-scroll opacity-0 translate-y-8 transition-all duration-700 delay-150 grid grid-cols-1 lg:grid-cols-2 gap-6 mt-12 mb-10">

          {/* 좌측 - 기존 방식 */}
          <div className="rounded-2xl overflow-hidden border border-gray-200">
            {/* 헤더 바 */}
            <div className="bg-gray-700 px-7 py-5 flex items-center justify-between">
              <div>
                <p className="text-gray-400 text-xs font-bold tracking-widest uppercase mb-1">기존 방식</p>
                <h3 className="text-white font-extrabold text-lg">기존 Roof Cap 방식</h3>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="bg-red-500/20 border border-red-500/40 text-red-400 text-xl font-black px-4 py-1.5 rounded-xl">
                  5단계
                </span>
              </div>
            </div>

            {/* 스텝 목록 */}
            <div className="bg-gray-50 px-7 py-6 space-y-3">
              {oldSteps.map((step, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-4 rounded-xl px-4 py-4 border transition-all ${
                    step.deleted
                      ? 'bg-red-50 border-red-200/70'
                      : 'bg-white border-gray-200'
                  }`}
                >
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${
                    step.deleted ? 'bg-red-100' : 'bg-gray-200'
                  }`}>
                    <i className={`${step.icon} text-base ${step.deleted ? 'text-red-400' : 'text-gray-500'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`text-[10px] font-bold tracking-widest ${step.deleted ? 'text-red-400' : 'text-gray-400'}`}>
                        {step.num}
                      </span>
                      {step.deleted && (
                        <span className="text-[9px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded-full tracking-wide">
                          삭제됨
                        </span>
                      )}
                    </div>
                    <p className={`text-sm font-semibold mt-0.5 ${step.deleted ? 'text-red-500 line-through' : 'text-gray-700'}`}>
                      {step.title}
                    </p>
                    {step.sub && (
                      <p className={`text-xs mt-0.5 ${step.deleted ? 'text-red-400/70 line-through' : 'text-gray-400'}`}>
                        {step.sub}
                      </p>
                    )}
                  </div>
                  {step.deleted && (
                    <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                      <i className="ri-close-circle-fill text-red-400 text-lg" />
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* 우측 - Smart Cap */}
          <div className="rounded-2xl overflow-hidden border border-emerald-200">
            {/* 헤더 바 */}
            <div className="bg-[#1B2A4A] px-7 py-5 flex items-center justify-between">
              <div>
                <p className="text-emerald-400 text-xs font-bold tracking-widest uppercase mb-1">Smart Cap 방식</p>
                <h3 className="text-white font-extrabold text-lg">Smart Cap 방식</h3>
              </div>
              <div className="flex flex-col items-end gap-2">
                <span className="bg-emerald-400/20 border border-emerald-400/40 text-emerald-300 text-xl font-black px-4 py-1.5 rounded-xl">
                  2단계
                </span>
                <span className="bg-[#FF6B35] text-white text-[10px] font-extrabold px-3 py-0.5 rounded-full tracking-wider">
                  추천
                </span>
              </div>
            </div>

            {/* 스텝 목록 */}
            <div className="bg-gradient-to-b from-emerald-50/60 to-white px-7 py-6 space-y-3">
              {newSteps.map((step, i) => (
                <div
                  key={i}
                  className={`flex items-start gap-4 rounded-xl px-4 py-4 border ${
                    step.isLast
                      ? 'bg-emerald-500 border-emerald-600'
                      : 'bg-white border-emerald-200'
                  }`}
                >
                  <div className={`w-9 h-9 flex items-center justify-center rounded-lg flex-shrink-0 ${
                    step.isLast ? 'bg-white/20' : 'bg-emerald-100'
                  }`}>
                    <i className={`${step.icon} text-base ${step.isLast ? 'text-white' : 'text-emerald-600'}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className={`text-[10px] font-bold tracking-widest ${step.isLast ? 'text-emerald-100' : 'text-emerald-600'}`}>
                      {step.num}
                    </span>
                    <p className={`text-sm font-bold mt-0.5 ${step.isLast ? 'text-white' : 'text-gray-800'}`}>
                      {step.title}
                    </p>
                    {step.sub && (
                      <p className={`text-xs mt-0.5 font-semibold ${step.isLast ? 'text-emerald-100' : 'text-emerald-500'}`}>
                        {step.sub}
                      </p>
                    )}
                  </div>
                  <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                    <i className={`ri-check-line text-lg font-bold ${step.isLast ? 'text-white' : 'text-emerald-500'}`} />
                  </div>
                </div>
              ))}

              {/* 절감 단계 표시 */}
              <div className="pt-4 border-t border-emerald-100 flex items-center gap-3">
                <div className="w-7 h-7 flex items-center justify-center rounded-full bg-emerald-100 border border-emerald-300 flex-shrink-0">
                  <i className="ri-check-line text-emerald-600 text-sm" />
                </div>
                <p className="text-sm text-gray-600 font-medium">
                  <span className="text-emerald-600 font-bold">3단계 공정 완전 삭제됨</span>
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 하단 강조 배너 */}
        <div className="animate-on-scroll opacity-0 translate-y-6 transition-all duration-700 delay-300">
          <div className="relative bg-[#1B2A4A] rounded-2xl px-8 py-7 flex flex-col md:flex-row items-center justify-center gap-4 overflow-hidden">
            {/* 배경 장식 */}
            <div className="absolute left-0 top-0 w-40 h-full bg-gradient-to-r from-[#FF6B35]/10 to-transparent" />
            <div className="absolute right-0 top-0 w-40 h-full bg-gradient-to-l from-emerald-500/10 to-transparent" />

            <div className="relative z-10 flex items-center gap-4 flex-wrap justify-center">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-red-500/15 border border-red-500/25">
                  <i className="ri-delete-bin-fill text-red-400 text-base" />
                </span>
                <span className="text-white font-extrabold text-xl md:text-2xl">3단계 공정 삭제</span>
              </div>

              <span className="text-gray-400 text-xl font-light">=</span>

              <div className="flex items-center gap-3">
                <span className="w-10 h-10 flex items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/25">
                  <i className="ri-rocket-2-fill text-emerald-400 text-base" />
                </span>
                <span className="text-emerald-400 font-extrabold text-xl md:text-2xl">시공 속도 50% 향상</span>
              </div>
            </div>
          </div>
        </div>

      </div>
    </section>
  );
};

export default ProcessComparisonSection;
